import { useRef, useCallback, useEffect } from "react";
import { motion, useMotionValue } from "framer-motion";
import { SmartImage } from "../ui/SmartImage";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_TOGGLE = 2.5;
const SWIPE_DIST = 45;
const TAP_MS = 300;
const TAP_PX = 40;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export const ZoomableImage = ({ src, alt, label, active = true, onZoomedChange, onSwipe }) => {
  const containerRef = useRef(null);
  const scale = useMotionValue(1);
  const scaleRef = useRef(1);
  const tx = useMotionValue(0);
  const ty = useMotionValue(0);
  const zoomed = useRef(false);
  const ptrs = useRef(new Map());
  const session = useRef(null);
  const pan = useRef(null);
  const pinch = useRef(null);
  const lastTap = useRef({ t: 0, x: 0, y: 0 });

  const setZoomed = useCallback(
    (z) => {
      if (zoomed.current !== z) {
        zoomed.current = z;
        onZoomedChange?.(z);
      }
    },
    [onZoomedChange]
  );

  const applyZoom = useCallback(
    (next, anchorX, anchorY) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const z = clamp(next, MIN_ZOOM, MAX_ZOOM);
      if (z === scaleRef.current) return;
      const cx0 = rect.left + rect.width / 2;
      const cy0 = rect.top + rect.height / 2;
      const k = z / scaleRef.current;
      const ntx = (1 - k) * (anchorX - cx0) + k * tx.get();
      const nty = (1 - k) * (anchorY - cy0) + k * ty.get();
      const mx = (rect.width / 2) * (z - 1);
      const my = (rect.height / 2) * (z - 1);
      scale.set(z);
      tx.set(clamp(ntx, -mx, mx));
      ty.set(clamp(nty, -my, my));
      scaleRef.current = z;
      setZoomed(z > 1.001);
    },
    [scale, tx, ty, setZoomed]
  );

  const reset = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    scale.set(1);
    tx.set(0);
    ty.set(0);
    scaleRef.current = 1;
    setZoomed(false);
    pinch.current = null;
    pan.current = null;
    session.current = null;
    ptrs.current.clear();
    void rect;
  }, [scale, tx, ty, setZoomed]);

  useEffect(() => {
    if (active) reset();
  }, [active, reset]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      applyZoom(scaleRef.current * Math.exp(-e.deltaY * 0.002), e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [applyZoom]);

  const toggleZoom = () => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    applyZoom(scaleRef.current > 1.001 ? MIN_ZOOM : ZOOM_TOGGLE, cx, cy);
  };

  const onPointerDown = (e) => {
    const id = e.pointerId;
    ptrs.current.set(id, { x: e.clientX, y: e.clientY });
    try {
      e.currentTarget.setPointerCapture(id);
    } catch {
      /* noop */
    }
    if (ptrs.current.size === 1) {
      session.current = { id, startX: e.clientX, startY: e.clientY, moved: false };
      if (zoomed.current) {
        pan.current = { id, lastX: e.clientX, lastY: e.clientY };
        e.preventDefault();
        e.stopPropagation();
      }
    } else if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()];
      pinch.current = {
        startDist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        startZoom: scaleRef.current,
      };
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onPointerMove = (e) => {
    const id = e.pointerId;
    if (!ptrs.current.has(id)) return;
    ptrs.current.set(id, { x: e.clientX, y: e.clientY });

    if (ptrs.current.size === 2 && pinch.current) {
      const [a, b] = [...ptrs.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      applyZoom(pinch.current.startZoom * (dist / pinch.current.startDist), midX, midY);
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (pan.current && pan.current.id === id) {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - pan.current.lastX;
      const dy = e.clientY - pan.current.lastY;
      const mx = (rect.width / 2) * (scaleRef.current - 1);
      const my = (rect.height / 2) * (scaleRef.current - 1);
      tx.set(clamp(tx.get() + dx, -mx, mx));
      ty.set(clamp(ty.get() + dy, -my, my));
      pan.current.lastX = e.clientX;
      pan.current.lastY = e.clientY;
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (session.current && session.current.id === id) {
      const s = session.current;
      if (Math.hypot(e.clientX - s.startX, e.clientY - s.startY) > 8) s.moved = true;
    }
  };

  const onPointerEnd = (e) => {
    const id = e.pointerId;
    ptrs.current.delete(id);

    if (pinch.current) {
      pinch.current = null;
      if (ptrs.current.size === 1) {
        const [r] = [...ptrs.current.values()];
        pan.current = { id: r.id, lastX: r.x, lastY: r.y };
      }
    } else {
      if (pan.current && pan.current.id === id) pan.current = null;
    }

    if (session.current && session.current.id === id) {
      const s = session.current;
      const now = Date.now();
      if (ptrs.current.size === 0 && !s.moved && zoomed.current === false && e.pointerType !== "mouse") {
        const prev = lastTap.current;
        if (now - prev.t < TAP_MS && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < TAP_PX) {
          lastTap.current = { t: 0, x: 0, y: 0 };
          toggleZoom();
        } else {
          lastTap.current = { t: now, x: e.clientX, y: e.clientY };
        }
      }
      if (zoomed.current === false && !s.moved && Math.abs(e.clientX - s.startX) > SWIPE_DIST) {
        onSwipe?.(e.clientX - s.startX > 0 ? -1 : 1);
      }
      session.current = null;
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden flex items-center justify-center"
      style={{ touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onDoubleClick={toggleZoom}
    >
      <motion.div style={{ scale, x: tx, y: ty }} className="flex h-full w-full items-center justify-center">
        <SmartImage
          src={src}
          alt={alt}
          label={label}
          draggable={false}
          className="max-h-full max-w-full select-none object-contain pointer-events-none"
        />
      </motion.div>
    </div>
  );
};

export default ZoomableImage;